import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { get } from '../utils/api';
import { referralRegister } from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../media/Transparent-Star-Only-Logo.png';

/**
 * Public referral landing page (/r/<code>). Greets with the referrer's first name, states the
 * give-get (25% off the first month), collects name / email / mobile, creates the account tagged
 * as a referral and drops the parent straight into onboarding — same mechanics as StaffSignup.
 */
export default function ReferralSignup() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [referrerFirstName, setReferrerFirstName] = useState('');
  const [form, setForm] = useState({ name: '', email: '', mobileNumber: '' });
  const [error, setError] = useState('');
  const [useLogin, setUseLogin] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await get(`/referrals/code/${encodeURIComponent(code || '')}`);
        if (cancelled) return;
        setValid(!!res?.valid);
        setReferrerFirstName(res?.referrerFirstName || '');
      } catch (e) {
        if (!cancelled) setValid(false);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setUseLogin(false);
    setSubmitting(true);
    const res = await referralRegister({ ...form, code });
    setSubmitting(false);
    if (res.success) {
      login(res.userData);
      navigate('/onboarding');
    } else if (res.status === 'use_login') {
      setUseLogin(true);
    } else {
      setError(res.message || 'Could not create your account.');
    }
  };

  const inputClass =
    'block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-base text-gray-900 placeholder-gray-400 focus:border-[#5C3BD1] focus:outline-none focus:ring-2 focus:ring-[#5C3BD1]/30';

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#5C3BD1] to-[#3F2A93] font-poppins px-4 py-10">
      <div className="mx-auto max-w-md rounded-2xl bg-white p-8 shadow-xl">
        <div className="mb-6 flex flex-col items-center text-center">
          <img src={Logo} alt="Nova Learning" className="mb-3 h-14 w-14" />
          {checking ? (
            <p className="text-gray-500">Checking your invite…</p>
          ) : valid ? (
            <>
              <h1 className="font-fredoka text-2xl font-bold text-[#2F1E6B]">
                {referrerFirstName ? `${referrerFirstName} invited you to Nova Learning` : "You've been invited to Nova Learning"}
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                Fun, live maths lessons that build real confidence — starting with a <strong>free trial</strong> and a full
                assessment report for your child.
              </p>
              <div className="mt-4 w-full rounded-xl bg-[#F3EFFD] px-4 py-3 text-sm font-semibold text-[#2F1E6B]">
                🎁 {referrerFirstName ? `${referrerFirstName}'s` : 'This'} invite gives you <span className="text-[#5C3BD1]">25% off your first month</span>
              </div>
            </>
          ) : (
            <>
              <h1 className="font-fredoka text-2xl font-bold text-[#2F1E6B]">This invite link isn't valid</h1>
              <p className="mt-2 text-sm text-gray-600">
                Please check the link your friend sent you, or{' '}
                <Link to="/register" className="font-semibold text-[#5C3BD1] underline">
                  sign up here
                </Link>
                .
              </p>
            </>
          )}
        </div>

        {!checking && valid && (
          <form onSubmit={onSubmit} className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              Your name
              <input className={`${inputClass} mt-1`} type="text" value={form.name} onChange={update('name')} required autoComplete="name" />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Email
              <input className={`${inputClass} mt-1`} type="email" value={form.email} onChange={update('email')} required autoComplete="email" />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Mobile number
              <input className={`${inputClass} mt-1`} type="tel" value={form.mobileNumber} onChange={update('mobileNumber')} required autoComplete="tel" />
            </label>

            {useLogin && (
              <p className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
                An account with this email already exists.{' '}
                <Link to="/login" className="font-semibold underline">
                  Log in instead
                </Link>
                .
              </p>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg bg-[#5C3BD1] px-5 py-3 text-base font-semibold text-white transition hover:bg-[#4b30ad] disabled:opacity-60"
            >
              {submitting ? 'Creating your account…' : 'Start my free trial'}
            </button>
            <p className="text-center text-xs text-gray-500">
              Your 25% discount is applied automatically to your first month's subscription. No card needed for the trial.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
